"use client"

import type React from "react"

import { useState, useEffect, useRef } from "react"
import { ChevronDown, ChevronUp } from "lucide-react"

interface CollapsibleSectionProps {
  title: string
  children: React.ReactNode
  className?: string
  mobileBreakpoint?: number
  defaultExpanded?: boolean
}

export function CollapsibleSection({
  title,
  children,
  className = "",
  mobileBreakpoint = 768,
  defaultExpanded = true,
}: CollapsibleSectionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  const contentRef = useRef<HTMLDivElement>(null)

  // Set the initial expanded state based on screen width after component mounts
  useEffect(() => {
    setIsExpanded(window.innerWidth >= mobileBreakpoint || defaultExpanded)

    // Optional: Update on resize if you want it to adapt when users rotate devices
    const handleResize = () => {
      // Only change the state if the user hasn't manually toggled it
      const newExpanded = window.innerWidth >= mobileBreakpoint
      setIsExpanded((prev) => {
        // Only update if the user hasn't interacted with it yet
        if (prev === window.innerWidth >= mobileBreakpoint) {
          return newExpanded
        }
        return prev
      })
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [mobileBreakpoint, defaultExpanded])

  return (
    <div className={`bg-blue-50 rounded-lg overflow-hidden shadow-sm ${className}`}>
      <div
        className="p-4 flex items-center justify-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <h2 className="font-semibold text-blue-800">{title}</h2>
        <button
          className="text-blue-600 hover:text-blue-800 transition-colors"
          aria-label={isExpanded ? "Collapse information" : "Expand information"}
        >
          {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </button>
      </div>

      <div
        ref={contentRef}
        className="collapsible-section"
        style={{
          maxHeight: isExpanded ? (contentRef.current ? contentRef.current.scrollHeight + "px" : "1000px") : "0px",
        }}
      >
        <div className="p-4 pt-0">{children}</div>
      </div>
    </div>
  )
}
